import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import useAuthenticate from "../authentication/authenticate.js";

const CreateRequestForm = ({ property, onRequestCreated, onClose }) => {
  const { user } = useAuthenticate();

  const [propertyAccounts, setPropertyAccounts] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [units, setUnits] = useState([]);
  const [role, setRole] = useState(false);

  const isUnitObject = !!property?.property?._id;

  const propertyId = isUnitObject ? property?.property?._id : property?._id || property?.selectedProperty?.value;

  const [formData, setFormData] = useState({
    unit: isUnitObject ? property._id : '',
    requestedBy: '',
    managedBy: [],
    assignees: [],
    type: '',
    status: 'Pending',
    description: '',
    details: {
      priority: 'High',
      title: '',
      requestedDate: new Date().toISOString(),
    },
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('Property prop:', property);
  }, [property]);

  useEffect(() => {
    if (user?.id) {
      const role = Object.keys(user.active || {})[0]; // manager, owner, etc.
      setRole(role);

      const activeAccountId = user.active?.[role] ?? null;
      const email = user.email ?? "";

      setFormData(prev => ({ ...prev, requestedBy: activeAccountId }));
      setCurrentUserEmail(email);
    }
  }, [user]);

  useEffect(() => {
    const fetchUnitsAndAccounts = async () => {
      try {
        const res = await axios.get(`${config.API_URL}/v2/unit`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        setUnits(res.data);
        console.log('Fetched units:', res.data);
      } catch (err) {
        console.error("Error fetching user units:", err);
        setError("Could not fetch unit information.");
      }
    };

    fetchUnitsAndAccounts();
  }, [property._id]);

  useEffect(() => {
    const fetchPropertyAccounts = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/v2/role/propertyRole/property/${propertyId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        const accounts = response.data.map(item => item.account);
        setPropertyAccounts(accounts);
        console.log("Fetched property accounts:", accounts); 
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setError("Error fetching accounts. Please try again.");
      }
    };
    fetchPropertyAccounts();
  }, [propertyId]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(`${config.API_URL}/v2/request`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Request created successfully!');
        onRequestCreated?.(response.data);
        onClose();
      } else {
        setError('Failed to create the request.');
        toast.error('Failed to create the request.');
      }
    } catch (error) {
      setError('Error creating the request. Please try again.');
      toast.error('Error creating the request. Please try again.');
      console.error(error);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-popup" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h2 className="text-2xl text-black">Add Request</h2>
        <div className="popup-columns">
          <Autocomplete
            id="unit"
            name="unit"
            options={units.filter(
              (u) => u.property?._id === propertyId
            )}            
            getOptionLabel={(option) => option.unitNumber}
            value={units.find((u) => u._id === formData.unit) || null}
            onChange={async (event, newValue) => {
              if (!newValue) return;
              const selectedUnitId = newValue._id;
              setFormData((prev) => ({ ...prev, unit: selectedUnitId }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Unit" variant="outlined" fullWidth required />
            )}
            disabled={isUnitObject}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />

          <Autocomplete
            name="priority"
            value={formData.details.priority || ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  details: {
                    ...prevData.details,
                    priority: newValue,
                  },
                }));
              }
            }}
            options={['High', 'Medium', 'Low']}
            renderInput={(params) => (
              <TextField {...params} label="Priority" variant="outlined" fullWidth required />
            )}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
        </div>

        <TextField
          label="Request Title"
          name="title"
          fullWidth
          value={formData.details.title}
          onChange={(event) => {
            const newValue = event.target.value;
            setFormData((prevData) => ({
              ...prevData,
              details: {
                ...prevData.details,
                title: newValue,
              },
            }));
          }}
          sx={{ marginBottom: '1rem' }}
          required
        />

        <TextField
          label="Requested By"
          fullWidth
          value={currentUserEmail}
          disabled
          sx={{ marginBottom: '1rem' }}
        />

        {role === 'manager' && (
          <>
            <Autocomplete
              multiple
              id="managedBy"
              name="managedBy"
              options={propertyAccounts.filter((acc) => acc.type === "manager")}
              getOptionLabel={(option) => `(${option.type}) ${option.user.email}`}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={propertyAccounts.filter((acc) => acc.type === "manager" && formData.managedBy.includes(acc._id))}
              onChange={(event, newValue) => {
                const selectedIds = newValue.map((user) => user._id);
                setFormData((prevData) => ({
                  ...prevData,
                  managedBy: selectedIds,
                }));
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} label="Managed By" placeholder="Select Users" variant="outlined" fullWidth required />
              )}
              sx={{ width: '100%', marginBottom: '1rem' }}
            />

            <Autocomplete
              multiple
              id="assignees"
              name="assignees"
              options={propertyAccounts.filter((acc) => acc.type !== "owner" && acc.type !== "tenant")}
              getOptionLabel={(option) => `(${option.type}) ${option.user.email}`}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={propertyAccounts.filter((acc) => acc.type !== "owner" && acc.type !== "tenant" && formData.assignees.includes(acc._id))}
              onChange={(event, newValue) => {
                const selectedIds = newValue.map((user) => user._id);
                setFormData((prevData) => ({
                  ...prevData,
                  assignees: selectedIds,
                }));
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} label="Assignees" placeholder="Select Users" variant="outlined" fullWidth required />
              )}
              sx={{ width: '100%', marginBottom: '1rem' }}
            />
          </>
        )}

        <div className="popup-columns">
          <Autocomplete
            name="type"
            value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  type: newValue,
                }));
              }
            }}
            options={['Maintenance', 'Package', 'ChangeRequest', 'Complaint', 'AmenityBooking', 'Custom', 'Payment', 'Service']}
            renderInput={(params) => (
              <TextField {...params} label="Type" variant="outlined" fullWidth required />
            )}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
          <Autocomplete
            name="status"
            value={formData.status || ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  status: newValue,
                }));
              }
            }}
            options={['Pending', 'In-Progress', 'Completed', 'Rejected']}
            renderInput={(params) => (
              <TextField {...params} label="Status" variant="outlined" fullWidth required />
            )}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
        </div>

        <TextField
          label="Description"
          name="description"
          multiline
          fullWidth
          rows={3}
          value={formData.description}
          onChange={handleChange}
          sx={{ marginBottom: '1rem' }}
        />

        <button type="button" className="button" onClick={handleSubmit}>Add Request</button>
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default CreateRequestForm;